<script setup>
/**
 * Sidebar de navigation principale
 * Navigation hiérarchique avec support des groupes et items admin
 */
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLayoutStore } from '@/stores/layout'
import { navigationItems, notaryNavigationItems } from '@/router/navigation'

const route = useRoute()
const authStore = useAuthStore()
const layoutStore = useLayoutStore()

const isAdmin = computed(() => authStore.isAdmin)
const sourceNavigation = computed(() => authStore.isNotary ? notaryNavigationItems : navigationItems)

// Filtre récursivement les items selon les permissions, sans muter la source.
const filterTree = (items) =>
  items
    .filter((item) => !(item.adminOnly && !isAdmin.value))
    .map((item) => (item.items ? { ...item, items: filterTree(item.items) } : item))

const filteredNavigation = computed(() => filterTree(sourceNavigation.value))

// Le lien actif est celui dont le `to` correspond LE PLUS PRÉCISÉMENT à l'URL
// (correspondance la plus longue). Évite que « Biens » (/properties) reste
// surligné sur /properties/estimations, /properties/buyers, etc.
const collectTos = (items, acc = []) => {
  for (const it of items) {
    if (it.to) acc.push(it.to)
    if (it.items) collectTos(it.items, acc)
  }
  return acc
}
const allTos = computed(() => collectTos(sourceNavigation.value))

const activeTo = computed(() => {
  const path = route.path
  let best = ''
  for (const to of allTos.value) {
    if (!to || to === '/') continue
    if (path === to || path.startsWith(to + '/')) {
      if (to.length > best.length) best = to
    }
  }
  return best
})

const linkActive = (to) => !!to && (to === activeTo.value || route.path === to)

// Un item (ou groupe) est actif si lui-même ou un descendant porte le lien actif.
const isItemActive = (item) => {
  if (linkActive(item.to)) return true
  if (item.items?.some((child) => isItemActive(child))) return true
  return false
}

// Toggle groupe
const isGroupOpen = (item) => {
  return layoutStore.isGroupExpanded(item.name) || isItemActive(item)
}

const toggleGroup = (item) => {
  layoutStore.toggleGroup(item.name)
}

const handleItemClick = (item) => {
  if (item.items) {
    toggleGroup(item)
  }
}
</script>

<template>
<aside class="sidebar" :class="{ 'sidebar--collapsed': layoutStore.isSidebarCollapsed }">
    <!-- Logo -->
    <div class="sidebar-header">
      <router-link to="/dashboard" class="logo-link">
        <img
          :src="layoutStore.isSidebarCollapsed ? '/assets/images/symbole.svg' : '/assets/images/logo.svg'"
          alt="Prix du Marché"
          class="logo-img"
          :class="{ 'logo-img--collapsed': layoutStore.isSidebarCollapsed }"
        />
      </router-link>
      
      <!-- Bouton toggle (desktop uniquement) -->
      <button 
        v-if="!layoutStore.isMobile"
        class="toggle-btn"
        @click="layoutStore.toggleSidebar"
        :aria-label="layoutStore.isSidebarCollapsed ? 'Ouvrir le menu' : 'Fermer le menu'"
      >
        <i :class="layoutStore.isSidebarCollapsed ? 'pi pi-angle-right' : 'pi pi-angle-left'"></i>
      </button>
    </div>
    
    <!-- Navigation -->
    <nav class="sidebar-nav" :aria-label="layoutStore.isSidebarCollapsed ? undefined : 'Navigation principale'">
      <ul class="nav-list">
        <li
          v-for="item in filteredNavigation"
          :key="item.name"
          class="nav-item"
          :class="{
            'nav-item--group': item.items,
            'nav-item--active': isItemActive(item),
            'nav-item--expanded': isGroupOpen(item),
          }"
        >
          <!-- Item simple avec route -->
          <router-link
            v-if="!item.items && item.to"
            :to="item.to"
            class="nav-link"
            :class="{ 'nav-link--active': isItemActive(item) }"
            @click="layoutStore.closeMobileSidebar"
          >
            <i v-if="item.icon" :class="['nav-icon', item.icon]"></i>
            <span v-if="!layoutStore.isSidebarCollapsed" class="nav-text">
              {{ item.name }}
            </span>
          </router-link>
          
          <!-- Titre de section sans route -->
          <div
            v-else-if="!item.items && !item.to"
            class="nav-link nav-link--section"
          >
            <i v-if="item.icon" :class="['nav-icon', item.icon]"></i>
            <span v-if="!layoutStore.isSidebarCollapsed" class="nav-text">
              {{ item.name }}
            </span>
          </div>
          
          <!-- Groupe de navigation -->
          <template v-else>
            <button
              class="nav-link nav-link--group"
              :class="{ 'nav-link--active': isItemActive(item) }"
              @click="handleItemClick(item)"
              :aria-expanded="isGroupOpen(item)"
            >
              <i v-if="item.icon" :class="['nav-icon', item.icon]"></i>
              <span v-if="!layoutStore.isSidebarCollapsed" class="nav-text">
                {{ item.name }}
              </span>
              <i 
                v-if="!layoutStore.isSidebarCollapsed" 
                :class="['pi', 'nav-chevron', isGroupOpen(item) ? 'pi-angle-down' : 'pi-angle-right']"
              ></i>
            </button>
            
            <!-- Sous-menu -->
            <ul 
              v-show="isGroupOpen(item)" 
              v-if="!layoutStore.isSidebarCollapsed"
              class="sub-nav-list"
            >
              <li
                v-for="child in item.items"
                :key="child.name"
                class="sub-nav-item"
                :class="{ 'sub-nav-item--group': child.items }"
              >
                <!-- Sous-item avec route -->
                <router-link
                  v-if="child.to"
                  :to="child.to"
                  class="sub-nav-link"
                  :class="{ 'sub-nav-link--active': linkActive(child.to) }"
                  @click="layoutStore.closeMobileSidebar"
                >
                  <span class="sub-nav-text">{{ child.name }}</span>
                </router-link>
                <!-- Sous-item sans route (titre de sous-groupe) -->
                <div
                  v-else
                  class="sub-nav-link sub-nav-link--header"
                >
                  <span class="sub-nav-text">{{ child.name }}</span>
                </div>
                <!-- Sous-sous-menu (niveau 3) -->
                <ul
                  v-if="child.items"
                  class="nested-nav-list"
                >
                  <li
                    v-for="nested in child.items"
                    :key="nested.name"
                    class="nested-nav-item"
                  >
                    <router-link
                      v-if="nested.to"
                      :to="nested.to"
                      class="nested-nav-link"
                      :class="{ 'nested-nav-link--active': linkActive(nested.to) }"
                      @click="layoutStore.closeMobileSidebar"
                    >
                      <span class="nested-nav-text">{{ nested.name }}</span>
                    </router-link>
                  </li>
                </ul>
              </li>
            </ul>
          </template>
        </li>
      </ul>
    </nav>
    
    <!-- Footer sidebar -->
    <div v-if="!layoutStore.isSidebarCollapsed" class="sidebar-footer">
      <div class="version">v1.0.0</div>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--primary-500);
  color: white;
}

/* Header blanc — en-tête en haut de la sidebar */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-4);
  height: var(--header-height);
  background-color: white;
  border-bottom: 1px solid var(--tertiary-200);
  position: relative;
}

/* Quand la sidebar est repliée : symbole centré + toggle qui chevauche le bord droit */
.sidebar--collapsed .sidebar-header {
  padding: var(--space-3);
  justify-content: center;
}

.sidebar--collapsed .toggle-btn {
  position: absolute;
  top: 50%;
  right: -18px;
  transform: translateY(-50%);
  z-index: 5;
}

.sidebar--collapsed .toggle-btn:hover {
  transform: translateY(-50%) scale(1.05);
}

.logo-link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--secondary);
  text-decoration: none;
  font-weight: var(--font-semibold);
  font-size: var(--text-lg);
}

.logo-link:hover {
  opacity: 0.85;
}

.logo-img {
  height: 36px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
  transition: all var(--transition-fast);
}

.logo-img--collapsed {
  height: 36px;
  width: 36px;
  max-width: 36px;
}

.logo-text {
  white-space: nowrap;
}

.toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background-color: var(--primary-500);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  font-size: var(--text-base);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.toggle-btn:hover {
  background-color: var(--primary-600);
  transform: scale(1.05);
}

.toggle-btn:active {
  transform: scale(0.95);
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-2);
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.nav-item {
  border-radius: var(--border-radius-lg);
  transition: background-color var(--transition-fast);
}

.nav-item--active {
  background-color: rgba(0, 0, 0, 0.12);
}

.nav-link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  color: white;
  text-decoration: none;
  border-radius: var(--border-radius-lg);
  transition: all var(--transition-fast);
  width: 100%;
  border: none;
  background: none;
  cursor: pointer;
  font-size: var(--text-sm);
}

.nav-link:hover {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
}

.nav-link--active {
  background-color: var(--secondary);
  color: white;
}

.nav-link--group {
  justify-content: space-between;
  flex-wrap: wrap;
}

.nav-icon {
  font-size: var(--text-lg);
  width: 24px;
  text-align: center;
  color: white;
  opacity: 0.95;
}

.nav-text {
  flex: 1;
  text-align: left;
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nav-chevron {
  font-size: var(--text-sm);
  transition: transform var(--transition-fast);
  opacity: 0.85;
}

.sub-nav-list {
  list-style: none;
  width: 100%;
  padding-left: var(--space-8);
  padding-top: var(--space-1);
  padding-bottom: var(--space-1);
}

.sub-nav-link {
  display: block;
  padding: var(--space-2) var(--space-3);
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  border-radius: var(--border-radius-md);
  font-size: var(--text-sm);
  transition: all var(--transition-fast);
}

.sub-nav-link:hover {
  color: white;
  background-color: rgba(255, 255, 255, 0.15);
}

.sub-nav-link--active {
  color: white;
  background-color: var(--secondary);
}

.sub-nav-link--header {
  color: rgba(255, 255, 255, 0.7);
  font-weight: var(--font-semibold);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-top: var(--space-3);
  padding-bottom: var(--space-1);
}

.sub-nav-link--header:hover {
  background: none;
  color: rgba(255, 255, 255, 0.7);
}

/* Niveau 3 - nested navigation */
.nested-nav-list {
  list-style: none;
  padding-left: var(--space-4);
  padding-top: var(--space-1);
}

.nested-nav-item {
  margin-bottom: var(--space-1);
}

.nested-nav-link {
  display: block;
  padding: var(--space-2) var(--space-3);
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  border-radius: var(--border-radius-md);
  font-size: var(--text-sm);
  transition: all var(--transition-fast);
}

.nested-nav-link:hover {
  color: white;
  background-color: rgba(255, 255, 255, 0.15);
}

.nested-nav-link--active {
  color: white;
  background-color: var(--secondary);
}

/* Section title styling */
.nav-link--section {
  color: rgba(255, 255, 255, 0.85);
  font-weight: var(--font-bold);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding-top: var(--space-5);
  padding-bottom: var(--space-2);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: var(--space-3);
  pointer-events: none;
}

.nav-link--section:hover {
  background: none;
  color: rgba(255, 255, 255, 0.85);
}

.sidebar-footer {
  padding: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.version {
  font-size: var(--text-xs);
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
}

/* Mode collapsed */
.sidebar:has(+ .main-wrapper .sidebar--collapsed) .nav-text,
.sidebar:has(+ .main-wrapper .sidebar--collapsed) .nav-chevron,
.sidebar:has(+ .main-wrapper .sidebar--collapsed) .sub-nav-list,
.sidebar:has(+ .main-wrapper .sidebar--collapsed) .sidebar-footer {
  display: none;
}
</style>
